import React, { useCallback, useEffect, useState } from 'react';
import { squareAPI } from '../services/api';
import Icon from './Icon';

function SquareIntegrationSettings({ companyInfo, darkMode = true }) {
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // ── Device mode (terminal / register) ────────────────────────
  const [deviceMode, setDeviceMode] = useState(null); // 'terminal', 'register', or null
  const [deviceModeLoading, setDeviceModeLoading] = useState(false);

  // ── Terminal device state ──────────────────────────────────────
  const [terminalDevices, setTerminalDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [pairingCode, setPairingCode] = useState(null);
  const [pairingLoading, setPairingLoading] = useState(false);

  // ── Square Bookings sync ───────────────────────────────────────
  const [syncBookingsLoading, setSyncBookingsLoading] = useState(false);


  const fetchStatus = async () => {
    try {
      setLoading(true);
      const response = await squareAPI.getStatus();
      setStatus(response.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load Square integration status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const squareFlag = params.get('square');
    const squareError = params.get('square_error');
    let shouldClean = false;

    if (squareFlag === 'connected') {
      setSuccess('Square account connected successfully! Your catalog, inventory, and payments are now synced.');
      fetchStatus();
      shouldClean = true;
    }
    if (squareError) {
      setError(`Square connection failed: ${squareError}`);
      shouldClean = true;
    }

    if (shouldClean) {
      params.delete('square');
      params.delete('square_error');
      const nextQuery = params.toString();
      const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}`;
      window.history.replaceState({}, '', nextUrl);
    }
  }, []);

  const handleConnect = async () => {
    try {
      setActionLoading(true);
      setError('');
      const response = await squareAPI.getConnectUrl();
      const url = response.data?.authorize_url;
      if (!url) {
        setError('Authorize URL is missing from backend response');
        setActionLoading(false);
        return;
      }
      window.location.href = url;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start Square OAuth flow');
      setActionLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect your Square account? This will disable catalog sync, inventory tracking, and payment processing.')) return;
    try {
      setActionLoading(true);
      await squareAPI.disconnect();
      setSuccess('Square account disconnected.');
      await fetchStatus();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to disconnect Square');
    } finally {
      setActionLoading(false);
    }
  };

  // ── Device mode helpers ──────────────────────────────────────
  const handleSetDeviceMode = async (mode) => {
    try {
      setDeviceModeLoading(true);
      setError('');
      await squareAPI.setDeviceMode(mode);
      setDeviceMode(mode);
      setSuccess(`Hardware mode set to: ${mode === 'terminal' ? 'Square Terminal' : 'Square Register / POS'}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to set device mode');
    } finally {
      setDeviceModeLoading(false);
    }
  };

  // ── Terminal device helpers ──────────────────────────────────────
  const loadDevices = useCallback(async (refresh = false) => {
    try {
      setDevicesLoading(true);
      const res = refresh
        ? await squareAPI.refreshDevices()
        : await squareAPI.listDevices();
      setTerminalDevices(res?.data?.devices || []);
    } catch (err) {
      console.error('Failed to load terminal devices', err);
    } finally {
      setDevicesLoading(false);
    }
  }, []);

  const handleCreatePairingCode = async () => {
    try {
      setPairingLoading(true);
      setError('');
      const res = await squareAPI.createPairingCode({});
      setPairingCode(res.data);
      setSuccess('Pairing code generated! Enter it on your Square Terminal.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate pairing code');
    } finally {
      setPairingLoading(false);
    }
  };

  const handleRefreshAfterPairing = async () => {
    setPairingCode(null);
    await loadDevices(true);
    if (terminalDevices.length > 0) {
      setSuccess('Terminal device found and ready!');
    }
  };

  // ── Square Bookings sync ───────────────────────────────────────
  const handleSyncBookings = async () => {
    try {
      setSyncBookingsLoading(true);
      setError('');
      const res = await squareAPI.syncBookings();
      const fetched = res?.data?.fetched ?? 0;
      const created = res?.data?.created ?? 0;
      const updated = res?.data?.updated ?? 0;
      if (fetched === 0) {
        setSuccess(
          'Square returned 0 bookings. Check that you have appointments in Square Appointments for today and the next 31 days.'
        );
      } else {
        setSuccess(`Bookings synced: ${fetched} from Square, ${created} new, ${updated} updated.`);
      }
      await fetchStatus();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to sync Square bookings');
    } finally {
      setSyncBookingsLoading(false);
    }
  };

  // Grant missing APPOINTMENTS_READ scope (no disconnect needed – Square only asks for the new permission)
  const handleGrantBookingsPermission = async () => {
    try {
      setActionLoading(true);
      setError('');
      const response = await squareAPI.getConnectUrl();
      const url = response.data?.authorize_url;
      if (url) window.location.href = url;
      else setError('Authorize URL missing');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to open Square authorization');
    } finally {
      setActionLoading(false);
    }
  };

  // Load devices + device mode when connected
  useEffect(() => {
    if (status?.connection?.status === 'connected') {
      // Device mode comes from status response
      const mode = status?.device_mode || status?.connection?.square_device_mode || null;
      setDeviceMode(mode);
      // Only load terminal devices if mode is terminal (or not yet set)
      if (mode !== 'register') {
        loadDevices();
      }
    }
  }, [status, loadDevices]);

  if (loading) {
    return (
      <div style={{
        padding: '40px 20px',
        textAlign: 'center',
        color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)'
      }}>
        <Icon name="loading" color="secondary" darkMode={darkMode} size={24} />
        <p style={{ marginTop: '12px', fontSize: '14px' }}>Loading integration status...</p>
      </div>
    );
  }

  const connection = status?.connection;
  const connected = connection?.status === 'connected';
  const tier = companyInfo?.subscription_tier || status?.company_tier || 'basic';
  const isEnterprise = tier === 'enterprise' || tier === 'lifetime';

  const syncStates = status?.sync_state || [];
  const connectedAt = connection?.connected_at || connection?.created_at;
  const missingAppointmentsScope = status?.missing_appointments_scope === true;

  return (
    <div>
      {/* Success banner */}
      {success && (
        <div style={{
          marginBottom: '20px',
          padding: '16px 20px',
          borderRadius: '14px',
          background: 'rgba(34, 197, 94, 0.12)',
          border: '1px solid rgba(34, 197, 94, 0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <Icon name="success" customColor="#16a34a" size={20} />
          <span style={{ color: '#16a34a', fontSize: '14px', fontWeight: '500', flex: 1 }}>{success}</span>
          <button onClick={() => setSuccess('')} style={{ background: 'none', border: 'none', color: '#16a34a', cursor: 'pointer', fontSize: '16px', padding: '4px' }}>x</button>
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div style={{
          marginBottom: '20px',
          padding: '16px 20px',
          borderRadius: '14px',
          background: 'rgba(239, 68, 68, 0.12)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <Icon name="error" customColor="#ef4444" size={20} />
          <span style={{ color: '#ef4444', fontSize: '14px', fontWeight: '500', flex: 1 }}>{error}</span>
          <button onClick={() => setError('')} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '16px', padding: '4px' }}>x</button>
        </div>
      )}

      {/* Main Integration Card */}
      <div style={{
        border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.1)',
        borderRadius: '20px',
        overflow: 'hidden',
        background: darkMode ? 'rgba(255,255,255,0.02)' : '#ffffff'
      }}>
        {/* Header with Square branding */}
        <div style={{
          padding: '28px',
          background: connected 
            ? (darkMode ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.08) 0%, rgba(34, 197, 94, 0.02) 100%)' : 'linear-gradient(135deg, rgba(34, 197, 94, 0.06) 0%, rgba(34, 197, 94, 0.02) 100%)')
            : (darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)'),
          borderBottom: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              {/* Square logo mark */}
              <div style={{
                width: '52px',
                height: '52px',
                borderRadius: '14px',
                background: '#000000',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
              }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="3" width="18" height="18" rx="3" fill="white"/>
                  <rect x="8" y="8" width="8" height="8" rx="1.5" fill="black"/>
                </svg>
              </div>
              <div>
                <h3 style={{ margin: 0, color: darkMode ? '#ffffff' : '#111827', fontSize: '19px', fontWeight: '700' }}>
                  Square
                </h3>
                <p style={{ margin: '4px 0 0 0', color: darkMode ? 'rgba(255,255,255,0.55)' : '#6b7280', fontSize: '13px', lineHeight: '1.5' }}>
                  Connect your Square account to create service tickets, process payments, sync your product catalog, and import bookings.
                </p>
              </div>
            </div>

            {/* Status badge */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              borderRadius: '999px',
              fontSize: '13px',
              fontWeight: '600',
              background: connected ? 'rgba(34, 197, 94, 0.15)' : (darkMode ? 'rgba(156,163,175,0.15)' : 'rgba(156,163,175,0.2)'),
              color: connected ? '#16a34a' : (darkMode ? 'rgba(255,255,255,0.6)' : '#6b7280'),
              flexShrink: 0
            }}>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: connected ? '#16a34a' : (darkMode ? 'rgba(255,255,255,0.3)' : '#9ca3af'),
                boxShadow: connected ? '0 0 8px rgba(34, 197, 94, 0.4)' : 'none'
              }} />
              {connected ? 'Connected' : 'Not connected'}
            </div>
          </div>
        </div>

        {/* Enterprise gate warning */}
        {!isEnterprise && (
          <div style={{
            margin: '20px 28px',
            padding: '16px 20px',
            borderRadius: '12px',
            background: 'rgba(245, 158, 11, 0.1)',
            border: '1px solid rgba(245, 158, 11, 0.25)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <Icon name="warning" customColor="#f59e0b" size={20} />
            <div>
              <div style={{ color: '#f59e0b', fontSize: '14px', fontWeight: '600' }}>Enterprise subscription required</div>
              <div style={{ color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280', fontSize: '12px', marginTop: '2px' }}>
                Upgrade to Enterprise to connect Square and unlock billing integrations.
              </div>
            </div>
          </div>
        )}

        <div style={{
          margin: '0 28px 20px',
          padding: '16px 18px',
          borderRadius: '12px',
          background: darkMode ? 'rgba(59,130,246,0.08)' : 'rgba(219,234,254,0.85)',
          border: darkMode ? '1px solid rgba(96,165,250,0.2)' : '1px solid rgba(37,99,235,0.35)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          flexWrap: 'wrap',
        }}>
          <div style={{ minWidth: '240px', flex: 1 }}>
            <div style={{ fontSize: '14px', fontWeight: '600', color: darkMode ? '#bfdbfe' : '#1e3a8a', marginBottom: '4px' }}>
              How Square integration works
            </div>
            <div style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.65)' : '#1f2937', lineHeight: '1.5' }}>
              Create service tickets with Square catalog items, then process payment when the job is done (Terminal, Cash, or Square POS). Orders created on your Square POS are automatically imported and matched to bikes via webhooks. Bookings from Square Appointments sync into your services list as upcoming jobs.
            </div>
          </div>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => { window.location.href = '/help?section=integrations-square'; }}
            style={{ padding: '8px 12px', fontSize: '12px', whiteSpace: 'nowrap' }}
          >
            Open integration help
          </button>
        </div>

        {/* Connected state - details */}
        {connected && (
          <div style={{ padding: '24px 28px' }}>
            {/* Connection details grid */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '12px',
              marginBottom: '20px'
            }}>
              <div style={{ 
                padding: '16px', 
                borderRadius: '12px', 
                background: darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)'
              }}>
                <div style={{ fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.45)' : '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600' }}>
                  Merchant ID
                </div>
                <div style={{ marginTop: '6px', fontSize: '14px', color: darkMode ? '#ffffff' : '#111827', fontWeight: '500' }}>
                  {connection.square_merchant_id || 'N/A'}
                </div>
              </div>
              <div style={{ 
                padding: '16px', 
                borderRadius: '12px', 
                background: darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)'
              }}>
                <div style={{ fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.45)' : '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600' }}>
                  Location
                </div>
                <div style={{ marginTop: '6px', fontSize: '14px', color: darkMode ? '#ffffff' : '#111827', fontWeight: '500' }}>
                  {connection.square_location_name || connection.square_location_id || 'N/A'}
                </div>
              </div>
              {connectedAt && (
                <div style={{ 
                  padding: '16px', 
                  borderRadius: '12px', 
                  background: darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                  border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)'
                }}>
                  <div style={{ fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.45)' : '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600' }}>
                    Connected since
                  </div>
                  <div style={{ marginTop: '6px', fontSize: '14px', color: darkMode ? '#ffffff' : '#111827', fontWeight: '500' }}>
                    {new Date(connectedAt).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' })}
                  </div>
                </div>
              )}
            </div>

            {/* ── Hardware Mode Selector ───────────────────────── */}
            <div style={{
              padding: '20px',
              borderRadius: '12px',
              background: darkMode ? 'rgba(59,130,246,0.06)' : 'rgba(59,130,246,0.04)',
              border: darkMode ? '1px solid rgba(59,130,246,0.15)' : '1px solid rgba(59,130,246,0.12)',
              marginBottom: '16px',
            }}>
              <div style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600', marginBottom: '12px' }}>
                Hardware Type
              </div>
              <div style={{ fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.55)' : '#6b7280', marginBottom: '14px', lineHeight: '1.5' }}>
                Select which Square hardware you use in your shop. This determines how Frame-Book integrates with your payment flow.
              </div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {/* Terminal option */}
                <button
                  type="button"
                  onClick={() => handleSetDeviceMode('terminal')}
                  disabled={deviceModeLoading}
                  style={{
                    flex: '1 1 200px', padding: '16px', borderRadius: '12px', textAlign: 'left',
                    background: deviceMode === 'terminal'
                      ? (darkMode ? 'rgba(34,197,94,0.12)' : 'rgba(34,197,94,0.08)')
                      : (darkMode ? 'rgba(255,255,255,0.04)' : '#fafafa'),
                    border: deviceMode === 'terminal'
                      ? '2px solid rgba(34,197,94,0.4)'
                      : (darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e5e7eb'),
                    cursor: deviceModeLoading ? 'not-allowed' : 'pointer',
                    color: darkMode ? '#fff' : '#111827',
                  }}
                >
                  <div style={{ fontSize: '15px', fontWeight: 700, marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Icon name="settings" customColor={deviceMode === 'terminal' ? '#16a34a' : (darkMode ? 'rgba(255,255,255,0.5)' : '#9ca3af')} size={18} />
                    Square Terminal
                  </div>
                  <div style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280', lineHeight: '1.5' }}>
                    Standalone terminal device. Frame-Book sends payment requests directly to the terminal.
                  </div>
                </button>
                {/* Register option */}
                <button
                  type="button"
                  onClick={() => handleSetDeviceMode('register')}
                  disabled={deviceModeLoading}
                  style={{
                    flex: '1 1 200px', padding: '16px', borderRadius: '12px', textAlign: 'left',
                    background: deviceMode === 'register'
                      ? (darkMode ? 'rgba(34,197,94,0.12)' : 'rgba(34,197,94,0.08)')
                      : (darkMode ? 'rgba(255,255,255,0.04)' : '#fafafa'),
                    border: deviceMode === 'register'
                      ? '2px solid rgba(34,197,94,0.4)'
                      : (darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e5e7eb'),
                    cursor: deviceModeLoading ? 'not-allowed' : 'pointer',
                    color: darkMode ? '#fff' : '#111827',
                  }}
                >
                  <div style={{ fontSize: '15px', fontWeight: 700, marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Icon name="payment" customColor={deviceMode === 'register' ? '#16a34a' : (darkMode ? 'rgba(255,255,255,0.5)' : '#9ca3af')} size={18} />
                    Square Register / POS
                  </div>
                  <div style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280', lineHeight: '1.5' }}>
                    Register or POS device. You create orders on the Square POS, Frame-Book imports the completed orders.
                  </div>
                </button>
              </div>
              {!deviceMode && (
                <div style={{ marginTop: '10px', fontSize: '12px', color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Icon name="warning" customColor="#f59e0b" size={14} />
                  Please select your hardware type to continue setup.
                </div>
              )}
            </div>

            {/* Sync capabilities */}
            <div style={{
              padding: '16px 20px',
              borderRadius: '12px',
              background: darkMode ? 'rgba(34, 197, 94, 0.06)' : 'rgba(34, 197, 94, 0.04)',
              border: darkMode ? '1px solid rgba(34, 197, 94, 0.15)' : '1px solid rgba(34, 197, 94, 0.12)'
            }}>
              <div style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600', marginBottom: '12px' }}>
                Active capabilities
              </div>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                {[
                  { label: 'Catalog Sync', icon: 'details', always: true },
                  { label: 'Inventory', icon: 'bike', always: true },
                  { label: 'Payments', icon: 'payment', always: true },
                  ...(!missingAppointmentsScope ? [{ label: 'Bookings', icon: 'details', always: true }] : []),
                  ...(deviceMode === 'terminal' ? [{ label: 'Terminal', icon: 'settings', always: true }] : []),
                  ...(deviceMode === 'register' ? [{ label: 'Order Import', icon: 'details', always: true }] : []),
                ].map((cap) => (
                  <div key={cap.label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      background: 'rgba(34, 197, 94, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Icon name="success" customColor="#16a34a" size={12} />
                    </div>
                    <span style={{ fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.8)' : '#374151', fontWeight: '500' }}>
                      {cap.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Square bookings – always visible when connected */}
            <div style={{
              marginTop: '16px',
              padding: '16px 20px',
              borderRadius: '12px',
              background: darkMode ? 'rgba(59,130,246,0.06)' : 'rgba(59,130,246,0.04)',
              border: darkMode ? '1px solid rgba(59,130,246,0.15)' : '1px solid rgba(59,130,246,0.12)',
            }}>
              <div style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600', marginBottom: '10px' }}>
                Square Bookings
              </div>
              <div style={{ fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.65)' : '#374151', marginBottom: '12px', lineHeight: '1.5' }}>
                Sync appointments from Square Appointments into the Frame-Book calendar.
              </div>
              {missingAppointmentsScope ? (
                <div>
                  <p style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.55)' : '#6b7280', marginBottom: '10px', lineHeight: '1.5' }}>
                    An extra permission is required for booking sync. No need to fully reconnect – Square will only ask for the missing permission.
                  </p>
                  <button
                    type="button"
                    onClick={handleGrantBookingsPermission}
                    disabled={actionLoading}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '6px',
                      padding: '8px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: '500',
                      background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                      color: '#fff', border: 'none',
                      cursor: actionLoading ? 'not-allowed' : 'pointer',
                      opacity: actionLoading ? 0.6 : 1,
                    }}
                  >
                    {actionLoading ? <Icon name="loading" customColor="#fff" size={14} /> : <Icon name="details" customColor="#fff" size={14} />}
                    {actionLoading ? 'Opening...' : 'Grant permission'}
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={handleSyncBookings}
                    disabled={syncBookingsLoading}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '6px',
                      padding: '8px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: '500',
                      background: darkMode ? 'rgba(59,130,246,0.15)' : 'rgba(59,130,246,0.1)',
                      color: '#3b82f6',
                      border: darkMode ? '1px solid rgba(59,130,246,0.3)' : '1px solid rgba(59,130,246,0.25)',
                      cursor: syncBookingsLoading ? 'not-allowed' : 'pointer',
                      opacity: syncBookingsLoading ? 0.6 : 1,
                    }}
                  >
                    {syncBookingsLoading ? (
                      <><Icon name="loading" customColor="#3b82f6" size={14} /> Syncing...</>
                    ) : (
                      <><Icon name="details" customColor="#3b82f6" size={14} /> Sync Square bookings</>
                    )}
                  </button>
                  <span style={{ fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.4)' : '#9ca3af' }}>
                    Today + next 31 days (Square limit)
                  </span>
                </div>
              )}
            </div>

            {/* Sync states */}
            {syncStates.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                <div style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.4)' : '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600', marginBottom: '10px' }}>
                  Sync Status
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '8px' }}>
                  {syncStates.map((s, idx) => (
                    <div key={idx} style={{
                      padding: '10px 14px',
                      borderRadius: '10px',
                      background: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                      border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <span style={{ fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.7)' : '#374151', textTransform: 'capitalize' }}>
                        {s.resource || s.resource_type || 'Unknown'}
                      </span>
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: s.last_error ? '#ef4444' : '#16a34a'
                      }} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Terminal Devices (only in terminal mode) ──────── */}
            {deviceMode === 'terminal' && <div style={{ marginTop: '24px' }}>
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                marginBottom: '12px',
              }}>
                <div style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.4)' : '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600' }}>
                  Terminal Devices
                </div>
                <button
                  type="button"
                  onClick={() => loadDevices(true)}
                  disabled={devicesLoading}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                    padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '500',
                    background: 'transparent',
                    color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280',
                    border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
                    cursor: devicesLoading ? 'not-allowed' : 'pointer',
                    opacity: devicesLoading ? 0.5 : 1,
                  }}
                >
                  {devicesLoading ? 'Loading...' : 'Refresh'}
                </button>
              </div>

              {/* Device list */}
              {terminalDevices.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                  {terminalDevices.map((device) => (
                    <div key={device.id} style={{
                      padding: '12px 16px', borderRadius: '10px',
                      background: darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
                      border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}>
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: darkMode ? '#fff' : '#111827' }}>
                          {device.name || device.id}
                        </div>
                        <div style={{ fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.4)' : '#9ca3af', marginTop: '2px' }}>
                          ID: {device.id}
                          {device.battery_percent != null && ` · Battery: ${device.battery_percent}%`}
                          {device.type && ` · ${device.type}`}
                        </div>
                      </div>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        padding: '4px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: 600,
                        background: ['Online', 'AVAILABLE', 'ACTIVE', 'ACTIVE_READY'].includes(device.status)
                          ? 'rgba(34,197,94,0.15)' : 'rgba(156,163,175,0.15)',
                        color: ['Online', 'AVAILABLE', 'ACTIVE', 'ACTIVE_READY'].includes(device.status)
                          ? '#16a34a' : (darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280'),
                      }}>
                        <div style={{
                          width: '6px', height: '6px', borderRadius: '50%',
                          background: ['Online', 'AVAILABLE', 'ACTIVE', 'ACTIVE_READY'].includes(device.status)
                            ? '#16a34a' : '#9ca3af',
                        }} />
                        {device.status || 'Unknown'}
                      </div>
                    </div>
                  ))}
                </div>
              ) : !devicesLoading && (
                <div style={{
                  padding: '16px', borderRadius: '10px', marginBottom: '12px',
                  background: darkMode ? 'rgba(245,158,11,0.06)' : 'rgba(245,158,11,0.04)',
                  border: `1px solid ${darkMode ? 'rgba(245,158,11,0.2)' : 'rgba(245,158,11,0.15)'}`,
                  fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.6)' : '#6b7280',
                }}>
                  No paired terminal devices found. Pair your Square Terminal below to enable terminal payments.
                </div>
              )}

              {/* Pairing code display */}
              {pairingCode && (
                <div style={{
                  padding: '20px', borderRadius: '12px', textAlign: 'center', marginBottom: '12px',
                  background: darkMode ? 'rgba(59,130,246,0.1)' : 'rgba(59,130,246,0.06)',
                  border: `2px solid ${darkMode ? 'rgba(59,130,246,0.35)' : '#3b82f6'}`,
                }}>
                  <div style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.55)' : '#6b7280', marginBottom: '6px' }}>
                    Enter this code on your Square Terminal:
                  </div>
                  <div style={{
                    fontSize: '36px', fontWeight: 800, letterSpacing: '8px',
                    color: darkMode ? '#60a5fa' : '#2563eb',
                    fontFamily: 'monospace', margin: '14px 0',
                  }}>
                    {pairingCode.code}
                  </div>
                  <div style={{
                    fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280',
                    textAlign: 'left', lineHeight: '1.7', whiteSpace: 'pre-line',
                    maxWidth: '400px', margin: '0 auto',
                  }}>
                    {pairingCode.instructions}
                  </div>
                  <button
                    type="button"
                    onClick={handleRefreshAfterPairing}
                    disabled={devicesLoading}
                    style={{
                      marginTop: '16px', padding: '10px 24px', borderRadius: '10px',
                      background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                      color: '#fff', border: 'none', fontWeight: 600, fontSize: '13px',
                      cursor: devicesLoading ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {devicesLoading ? 'Checking...' : "I've paired it — check now"}
                  </button>
                </div>
              )}

              {/* Pair button */}
              {!pairingCode && (
                <button
                  type="button"
                  onClick={handleCreatePairingCode}
                  disabled={pairingLoading}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                    padding: '10px 18px', borderRadius: '10px',
                    background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                    color: darkMode ? 'rgba(255,255,255,0.8)' : '#374151',
                    border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
                    fontSize: '13px', fontWeight: '500',
                    cursor: pairingLoading ? 'not-allowed' : 'pointer',
                    opacity: pairingLoading ? 0.6 : 1,
                  }}
                >
                  {pairingLoading ? 'Generating...' : '+ Pair new terminal device'}
                </button>
              )}
            </div>}

            {/* ── Register Mode Info ─────────────────────────────── */}
            {deviceMode === 'register' && (
              <div style={{
                marginTop: '24px', padding: '20px', borderRadius: '12px',
                background: darkMode ? 'rgba(59,130,246,0.06)' : 'rgba(59,130,246,0.03)',
                border: darkMode ? '1px solid rgba(59,130,246,0.15)' : '1px solid rgba(59,130,246,0.1)',
              }}>
                <div style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.4)' : '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600', marginBottom: '10px' }}>
                  Register / POS Mode
                </div>
                <div style={{ fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.65)' : '#374151', lineHeight: '1.7' }}>
                  <strong>How it works:</strong>
                  <br />
                  1. Create and process orders as usual on your Square Register / POS.
                  <br />
                  2. Frame-Book automatically imports completed orders via webhook.
                  <br />
                  3. On the Services page, match each imported order to a bike using its frame number.
                  <br />
                  4. If the Square order note or ticket name contains a frame number, it will be matched automatically.
                </div>
                <div style={{
                  marginTop: '12px', padding: '10px 14px', borderRadius: '8px',
                  background: darkMode ? 'rgba(34,197,94,0.08)' : 'rgba(34,197,94,0.05)',
                  border: darkMode ? '1px solid rgba(34,197,94,0.15)' : '1px solid rgba(34,197,94,0.1)',
                  fontSize: '12px', color: '#16a34a', display: 'flex', alignItems: 'center', gap: '8px',
                }}>
                  <Icon name="success" customColor="#16a34a" size={14} />
                  Tip: Add the frame number in the Square POS line item note for automatic matching.
                </div>
                <div style={{
                  marginTop: '10px', padding: '10px 12px', borderRadius: '8px',
                  background: darkMode ? 'rgba(99,102,241,0.06)' : 'rgba(99,102,241,0.04)',
                  border: darkMode ? '1px solid rgba(99,102,241,0.12)' : '1px solid rgba(99,102,241,0.1)',
                  fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.55)' : '#4b5563', lineHeight: '1.5',
                }}>
                  <strong>Can we push payments to the iPad POS?</strong> Square’s APIs only allow pushing a checkout to a <strong>Square Terminal</strong> device (Terminal API). Pushing to the Register/POS app on iPad would require a native iOS app using the Point of Sale API; a web app cannot open the POS checkout from the browser. So with Register we only import completed orders; with Terminal we can send the payment to the device.
                </div>

              </div>
            )}
          </div>
        )}

        {/* Error state */}
        {connection?.status === 'error' && (
          <div style={{
            margin: '20px 28px',
            padding: '16px 20px',
            borderRadius: '12px',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.25)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <Icon name="error" customColor="#ef4444" size={20} />
            <div>
              <div style={{ color: '#ef4444', fontSize: '14px', fontWeight: '600' }}>Connection issue detected</div>
              <div style={{ color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280', fontSize: '12px', marginTop: '2px' }}>
                Your Square connection has an error. Please disconnect and reconnect to resolve.
              </div>
            </div>
          </div>
        )}

        {/* Not connected - features preview */}
        {!connected && isEnterprise && (
          <div style={{ padding: '24px 28px' }}>
            <div style={{ fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280', marginBottom: '16px', fontWeight: '500' }}>
              Connecting Square unlocks:
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
              {[
                { icon: 'details', title: 'Service Tickets', desc: 'Create work-order tickets with Square catalog items, then pay when the job is done' },
                { icon: 'payment', title: 'Payment Processing', desc: 'Process payments via Square Terminal, Cash, or mark as paid from Square POS' },
                { icon: 'bike', title: 'Catalog & Inventory', desc: 'Sync your Square product catalog and stock levels into Frame-Book' },
                { icon: 'settings', title: 'Bookings Import', desc: 'Pull Square Appointments into your services list as upcoming jobs' }
              ].map((feature) => (
                <div key={feature.title} style={{
                  padding: '16px',
                  borderRadius: '12px',
                  background: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                  border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)'
                }}>
                  <Icon name={feature.icon} color="secondary" darkMode={darkMode} size={18} />
                  <div style={{ marginTop: '8px', fontSize: '14px', color: darkMode ? '#ffffff' : '#111827', fontWeight: '600' }}>
                    {feature.title}
                  </div>
                  <div style={{ marginTop: '4px', fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280', lineHeight: '1.4' }}>
                    {feature.desc}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div style={{ 
          padding: '20px 28px', 
          borderTop: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
          display: 'flex', 
          gap: '10px', 
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {!connected ? (
              <button
                type="button"
                onClick={handleConnect}
                disabled={actionLoading || !isEnterprise}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 24px',
                  background: actionLoading || !isEnterprise ? (darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)') : 'linear-gradient(135deg, #1A2811 0%, #4A8C18 100%)',
                  color: actionLoading || !isEnterprise ? (darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)') : '#ffffff',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: actionLoading || !isEnterprise ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {actionLoading ? (
                  <>
                    <Icon name="loading" customColor="#ffffff" size={16} />
                    Connecting...
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <rect x="3" y="3" width="18" height="18" rx="3" fill="currentColor"/>
                      <rect x="8" y="8" width="8" height="8" rx="1.5" fill={actionLoading || !isEnterprise ? (darkMode ? '#1a1a2e' : '#ffffff') : '#1A2811'}/>
                    </svg>
                    Connect Square Account
                  </>
                )}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleDisconnect}
                disabled={actionLoading}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 18px',
                  background: darkMode ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.08)',
                  color: '#ef4444',
                  border: '1px solid rgba(239, 68, 68, 0.25)',
                  borderRadius: '10px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: actionLoading ? 'not-allowed' : 'pointer',
                  opacity: actionLoading ? 0.5 : 1
                }}
              >
                {actionLoading ? 'Disconnecting...' : 'Disconnect'}
              </button>
            )}
            <button 
              type="button" 
              onClick={fetchStatus} 
              disabled={loading || actionLoading}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '10px 18px',
                background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                color: darkMode ? 'rgba(255,255,255,0.7)' : '#4b5563',
                border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
                borderRadius: '10px',
                fontSize: '13px',
                fontWeight: '500',
                cursor: loading || actionLoading ? 'not-allowed' : 'pointer',
                opacity: loading || actionLoading ? 0.5 : 1
              }}
            >
              <Icon name="loading" color="secondary" darkMode={darkMode} size={14} />
              Refresh
            </button>
          </div>

          {/* Security note */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '6px',
            fontSize: '11px',
            color: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.35)'
          }}>
            <Icon name="security" color="muted" darkMode={darkMode} size={12} />
            Secured with OAuth 2.0 encryption
          </div>
        </div>
      </div>
    </div>
  );
}

export default SquareIntegrationSettings;
